import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {StyledPayNowForm, StyledPayNowFormButton} from './StyledPayNowForm';
import AddPaymentForm from '../../TransactionsPage/AddPaymentForm';
import VendorEditor from '../../VendorsPage/VendorEditor/VendorEditor';
import {objectHelpers} from '../../../../utils/helpers';

const PayNowForm = ({
  clearAfterSubmit = true,
  invoice,
  onSuccess,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [isOpenVendorEditor, setIsOpenVendorEditor] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payeeData, setPayeeData] = useState({ vendor: null, beneficiary: null, beneficiaryValid: null });

  const formRef = useRef(null);
  const vendorSelectorRef = useRef(null);

  const disabledSubmitButton = useMemo(() => {
    if (!payeeData.vendor) return false;
    if (!payeeData.beneficiary) return false;
    return !payeeData.beneficiaryValid;
  }, [payeeData]);

  const isBankInfoMissing = useMemo(() => payeeData.vendor && !payeeData.beneficiary, [payeeData]);

  const initialFormData = useMemo(() => ({
    price: invoice?.amount,
    vendor_local_id: invoice?.vendor_local_id
  }), [invoice]);

  useEffect(() => {
    const vendorId = initialFormData.vendor_local_id;
    if (vendorId && vendorSelectorRef.current) {
      vendorSelectorRef.current.loadVendors(vendorId);
    }
  }, [initialFormData.vendor_local_id]);

  const handleOnButtonClick = () => {
    if (isBankInfoMissing) {
      setIsOpenVendorEditor(true);
    } else if (formRef.current) {
      formRef.current.submit();
    }
  }

  const handleOnSuccess = (response) => {
    setLoading(false);
    onSuccess?.(response)
  }

  const handleOnError = () => setLoading(false);

  const handleBeforeSubmit = () => setLoading(true);

  const handleCloseVendorEditor = () => setIsOpenVendorEditor(false);

  const handleSuccessVendorEdit = () => {
    handleCloseVendorEditor();
    if (vendorSelectorRef.current) {
      vendorSelectorRef.current.loadVendors(payeeData?.vendor?.vendor_id);
    }
  }

  const handleBeneficiaryChange = ({ vendor: newVendor, beneficiary: newBeneficiary }) => {
    const vendorChanged = !objectHelpers.arraysIsEqual(newVendor, payeeData.vendor);
    const beneficiaryChanged = newBeneficiary !== payeeData.beneficiary;

    if (!vendorChanged && !beneficiaryChanged) return;

    setPayeeData({
      vendor: vendorChanged ? newVendor : payeeData.vendor,
      beneficiary: beneficiaryChanged ? newBeneficiary : payeeData.beneficiary,
    });
  }

  const handleBeneficiaryValidationChange = (beneficiaryValid) => {
    if (payeeData.beneficiaryValid === beneficiaryValid) return;
    setPayeeData(prev => ({ ...prev, beneficiaryValid }));
  }

  return (
    <>
      <StyledPayNowForm
        {...rest}
      >
        <AddPaymentForm
          additionalFormValues={{invoice_id: invoice?.id}}
          clearAfterSubmit={clearAfterSubmit}
          initialFormData={initialFormData}
          beforeSubmit={handleBeforeSubmit}
          onBeneficiaryValidationChange={handleBeneficiaryValidationChange}
          onError={handleOnError}
          onSuccess={handleOnSuccess}
          size='half'
          showInvoiceButton={false}
          ref={formRef}
          vendorSelectorProps={{
            ref: vendorSelectorRef,
            onBeneficiaryChange: handleBeneficiaryChange
          }}
        />
        <StyledPayNowFormButton
          disabled={disabledSubmitButton}
          loading={loading}
          onClick={handleOnButtonClick}
          size='large'
          type='primary'
        >
          {isBankInfoMissing ? t('addBankingDetails') : `${t('pay')} ${t('now')}`}
        </StyledPayNowFormButton>
      </StyledPayNowForm>

      <VendorEditor
        mode='edit'
        open={isOpenVendorEditor}
        onCancel={handleCloseVendorEditor}
        onSuccess={handleSuccessVendorEdit}
        size='banking'
        vendorId={payeeData?.vendor?.vendor_id}
      />
    </>
  );
}

PayNowForm.propTypes = {
  clearAfterSubmit: PropTypes.bool,
  invoice: PropTypes.object,
  onSuccess: PropTypes.func,
}

export default PayNowForm;
